import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import type { SortOrder } from './SortableHeader';
import { useRouterState, useNavigate } from '@tanstack/react-router';
import { useIsFetching } from '@tanstack/react-query';
import { cn, viIncludes } from '@/lib/utils';
import { type FilterField, type ActiveFilters } from './FilterBar';
import { ERPPagination } from './ERPPagination';
import { Button } from '@/components/ui/button';
import { Download, Filter, Loader2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { TableProperties, LayoutGrid, CalendarDays } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { EntityLinkContext } from './EntityLink';
import { ContextualHelpButton } from '@/components/common/ContextualHelpButton';
import { MobileOverflowMenu, useMobileOverflow, rankAction } from './MobileOverflowMenu';
import { ToolbarSecondaryActions, filterVisibleSecondaryActions, type ToolbarSecondaryActionItem } from './ToolbarSecondaryActions';
import { TrashTabs, DeletedRecordsTable, useDeletedCount } from './ListTrashTab';
import { exportToExcel } from '@/lib/excel.utils';
import { usePreferredPageSize } from '@/hooks/usePreferredPageSize';

// ── Types ────────────────────────────────────────────────────────────────────

interface ListPageShellProps {
    /** Left sidebar (e.g. GroupSidebar / StatusSidebar) */
    sidebar?: React.ReactNode;
    /** Unit label for record count, e.g. "KH", "SP" */
    unitLabel?: string;
    /** Buttons on the right side of the toolbar (Add, Export, Import...) */
    actions?: React.ReactNode;
    /** Export handler — if provided, shows Export Excel button */
    onExport?: () => void;
    /** Loading state for `onExport` button */
    exportLoading?: boolean;
    /** Danh sách thao tác phụ gom vào menu `[··· Khác]` trên Desktop (trước nút chính `+ Thêm`) và tự động gộp vào menu `…` trên Mobile */
    secondaryActions?: ToolbarSecondaryActionItem[];
    /** Buộc tách thanh công cụ thành 2 hàng chuẩn EMS ngay cả khi trang không truyền `tabs` */
    forceTwoRows?: boolean;
    /** Nhãn phạm vi nhẹ bên trái Hàng 1 khi bật `forceTwoRows` mà trang không có `tabs` */
    scopeLabel?: React.ReactNode;
    /** Header filters (tags area) */
    headerFilters?: React.ReactNode;
    /** Header tabs (time filters, etc) rendered above toolbar */
    tabs?: React.ReactNode;
    /** Hàng tab chính riêng phía trên hàng lọc (VD: nhóm công đoạn), cuộn ngang trên mobile */
    primaryTabs?: React.ReactNode;
    /** Mã module thùng rác (VD 'orders'): thêm tab "Thùng rác" chuẩn, liệt kê và phục hồi bản ghi đã xoá của trang */
    trashModule?: string;
    /** @deprecated Không còn tác dụng: thanh công cụ luôn tách 2 hàng (tabs + thao tác / ngày + lọc + tag) */
    wrapToolbarOnMobile?: boolean;
    /** Optional date period filter rendered on the right side of row 2 */
    dateFilter?: React.ReactNode;
    /** Available views to toggle between */
    availableViews?: ('table' | 'kanban' | 'calendar')[];
    /** Current active view */
    activeView?: 'table' | 'kanban' | 'calendar';
    /** Callback when view is changed */
    onViewChange?: (view: 'table' | 'kanban' | 'calendar') => void;

    // ── Filter ──
    filterFields?: FilterField[];
    filterValues?: ActiveFilters;
    // onFilterChange is passed directly to InlineFilterRow by the parent
    /** Whether the inline filter row is visible */
    showFilter?: boolean;
    /** Toggle filter row visibility */
    onToggleFilter?: () => void;
    /** Clear all filters + header search */
    onClearAll?: () => void;

    // ── Pagination ──
    currentPage: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;

    // ── Table ──
    children: React.ReactNode;   // <Table> content (thead + tbody)
    /** Optional top banner pinned above scrollable area (KPI metrics, summary ribbons) */
    topBanner?: React.ReactNode;
    /** Whether the list is embedded in another view (hides toolbar and fixed height) */
    embedded?: boolean;
    /** Global loading state for the list page */
    isLoading?: boolean;
}

/**
 * Thanh tiến trình mảnh hiển thị khi danh sách đang tải lại (lọc, sắp xếp, phân trang).
 * Chỉ hiện sau 150ms để tránh nhấp nháy khi phản hồi nhanh, không chặn thao tác nhập liệu.
 */
function ListFetchingBar({ active }: { active: boolean }) {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (!active) {
            setVisible(false);
            return;
        }
        const t = setTimeout(() => setVisible(true), 150);
        return () => clearTimeout(t);
    }, [active]);
    if (!visible) return null;
    return (
        <div
            role="progressbar"
            aria-label="Đang tải dữ liệu"
            className="absolute top-0 left-0 right-0 h-0.5 z-30 bg-indigo-100 overflow-hidden pointer-events-none"
        >
            <div className="h-full w-full bg-indigo-500 animate-pulse" />
        </div>
    );
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * ListPageShell — ERP-standard list page layout.
 *
 * Layout:
 * ┌────────────────────────────────────────────────────────────┐
 * │ [Sidebar]  │  [Sticky Toolbar: Lọc + Export + actions]     │
 * │            │  [Optional Top Banner: KPI / Metrics Ribbon]  │
 * │            │  [Scrollable Table with inline filters]        │
 * │            │  [Sticky Pagination bottom]                    │
 * └────────────────────────────────────────────────────────────┘
 */
export function ListPageShell({
    sidebar,
    unitLabel = 'bản ghi',
    actions: pageActions,
    onExport: pageOnExport,
    exportLoading = false,
    secondaryActions: pageSecondaryActions,
    forceTwoRows = false,
    scopeLabel: pageScopeLabel,
    filterFields: pageFilterFields = [],
    filterValues = {},
    showFilter = false,
    onToggleFilter,
    onClearAll,
    currentPage,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    children: pageChildren,
    headerFilters: pageHeaderFilters,
    tabs: pageTabs,
    primaryTabs,
    dateFilter: pageDateFilter,
    topBanner: pageTopBanner,
    availableViews: pageAvailableViews = ['table'],
    activeView = 'table',
    onViewChange,
    embedded = false,
    isLoading = false,
    trashModule,
}: ListPageShellProps) {
    // Tab "Thùng rác" chuẩn: khi mở thì ẩn bộ lọc / thao tác / dải số liệu của trang, bảng thay bằng bản ghi đã xoá
    const [showTrash, setShowTrash] = useState(false);
    const { data: trashCount } = useDeletedCount(trashModule);
    const inTrash = !!trashModule && showTrash;
    const tabs = trashModule
        ? <TrashTabs showTrash={showTrash} onChange={setShowTrash} pageTabs={pageTabs} count={trashCount} />
        : pageTabs;
    const actions = inTrash ? undefined : pageActions;
    const onExport = inTrash ? undefined : pageOnExport;
    const secondaryActions = inTrash ? undefined : pageSecondaryActions;
    const scopeLabel = inTrash ? undefined : pageScopeLabel;
    const filterFields = inTrash ? [] : pageFilterFields;
    const headerFilters = inTrash ? undefined : pageHeaderFilters;
    const dateFilter = inTrash ? undefined : pageDateFilter;
    const topBanner = inTrash ? undefined : pageTopBanner;
    const availableViews = inTrash ? ['table' as const] : pageAvailableViews;
    const children = inTrash ? <DeletedRecordsTable module={trashModule!} unitLabel={unitLabel} /> : pageChildren;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const isAnyFetching = useIsFetching() > 0;
    const scrollRef = useRef<HTMLDivElement>(null);
    const toolbarRef = useRef<HTMLDivElement>(null);
    const lastScrollY = useRef(0);
    const [toolbarHidden, setToolbarHidden] = useState(false);

    // Count active filters
    const activeFilterCount = Object.values(filterValues).filter(v => {
        if (!v) return false;
        if (typeof v === 'string') return !!v;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'object') return !!(v as any).from || !!(v as any).to;
        return false;
    }).length;

    // ── Horizontal scroll indicators ───────────────────────────────────────
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);

    const checkScrollOverflow = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const scrollLeft = el.scrollLeft;
        const maxScroll = el.scrollWidth - el.clientWidth;
        const nextLeft = scrollLeft > 10;
        const nextRight = maxScroll - scrollLeft > 10;
        setCanScrollLeft(prev => prev === nextLeft ? prev : nextLeft);
        setCanScrollRight(prev => prev === nextRight ? prev : nextRight);
    }, []);

    // ── Mobile auto-hide toolbar on scroll & horizontal check with rAF throttle ──
    const scrollTicking = useRef(false);
    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el || embedded) return;

        if (!scrollTicking.current) {
            window.requestAnimationFrame(() => {
                checkScrollOverflow();
                if (window.innerWidth >= 768) {
                    setToolbarHidden(prev => !prev ? prev : false);
                } else {
                    const scrollY = el.scrollTop;
                    const diff = scrollY - lastScrollY.current;
                    if (diff > 4 && scrollY > 60) {
                        setToolbarHidden(prev => prev ? prev : true);
                    } else if (diff < -4) {
                        setToolbarHidden(prev => !prev ? prev : false);
                    }
                    lastScrollY.current = scrollY;
                }
                scrollTicking.current = false;
            });
            scrollTicking.current = true;
        }
    }, [embedded, checkScrollOverflow]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el || embedded) return;
        el.addEventListener('scroll', handleScroll, { passive: true });

        // Initial check scheduled in animation frame to avoid synchronous reflow during commit
        const rafId = window.requestAnimationFrame(() => {
            checkScrollOverflow();
        });

        let roTicking = false;
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => {
            if (!roTicking) {
                window.requestAnimationFrame(() => {
                    checkScrollOverflow();
                    roTicking = false;
                });
                roTicking = true;
            }
        }) : null;

        ro?.observe(el);
        if (el.firstElementChild) {
            ro?.observe(el.firstElementChild);
        }

        return () => {
            window.cancelAnimationFrame(rafId);
            el.removeEventListener('scroll', handleScroll);
            ro?.disconnect();
        };
    }, [handleScroll, embedded, checkScrollOverflow]);

    const hasFilters = filterFields.length > 0;

    // Điện thoại: tối đa 4 nút thao tác mỗi hàng, nút kém quan trọng vào "…" (tabs không gom, cuộn ngang để xem hết)
    const actionsOverflow = useMobileOverflow(rankAction, 4);
    // Xuất Excel mặc định: trang chưa có nút xuất riêng thì xuất đúng bảng đang hiển thị (bỏ cột chọn / thao tác, dòng nhóm)
    const [actionsEl, setActionsEl] = useState<HTMLElement | null>(null);
    const [pageHasExport, setPageHasExport] = useState(true);
    useEffect(() => {
        if (!actionsEl) return;
        // Bỏ qua chính nút xuất mặc định (nếu đếm nó sẽ ẩn / hiện lặp vô hạn, treo trang)
        const check = () => setPageHasExport([...actionsEl.querySelectorAll('button, a')].some((b) => !b.closest('[data-testid="list-auto-export"]') && /xuất|export/i.test((b as HTMLElement).innerText || b.getAttribute('title') || '')));
        check();
        const mo = new MutationObserver(check);
        mo.observe(actionsEl, { childList: true, subtree: true, characterData: true });
        return () => mo.disconnect();
    }, [actionsEl]);
    const exportVisibleTable = useCallback(async () => {
        const table = scrollRef.current?.querySelector('table');
        if (!table) return;
        const heads = [...table.querySelectorAll('thead tr:last-child th')].map((th) => (th as HTMLElement).innerText.replace(/\s+/g, ' ').trim());
        const keep = heads.map((h, i) => ({ h, i })).filter(({ h }) => h && !/^thao tác$/i.test(h));
        const rows = [...table.querySelectorAll('tbody tr')].filter((tr) => ![...tr.querySelectorAll('td')].some((td) => (td as HTMLTableCellElement).colSpan > 1));
        const data = rows.map((tr) => {
            const tds = [...tr.querySelectorAll('td')];
            return Object.fromEntries(keep.map(({ i }) => [`c${i}`, ((tds[i] as HTMLElement)?.innerText || '').replace(/\s+/g, ' ').trim()]));
        });
        const columns = keep.map(({ h, i }) => ({ key: `c${i}`, label: h, width: Math.min(40, Math.max(12, h.length + 4)) }));
        const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' }).format(new Date()).replace(/-/g, '');
        await exportToExcel(data, columns, `${String(unitLabel).replace(/\s+/g, '_')}_${today}`);
    }, [unitLabel]);
    const hasSecondaryExport = filterVisibleSecondaryActions(secondaryActions).some((it) => /xuất|export/i.test(it.label));
    const showAutoExport = !inTrash && !pageOnExport && !hasSecondaryExport && !pageHasExport;

    // Ngày (pos 0) + nút Lọc: hàng 1 khi trang không có tabs, hàng 2 (cạnh tag lọc) khi có tabs
    const dateAndFilterToggle = (
        <>
            {dateFilter && (
                <div className="date-filter-pos0 flex items-center gap-1.5 shrink-0">
                    {dateFilter}
                    <div className="h-5 w-px bg-slate-200 shrink-0 mx-0.5" />
                </div>
            )}

            {/* Filter toggle */}
            {hasFilters && onToggleFilter && (
                showFilter ? (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-[11px] sm:text-xs gap-1 border-red-200 text-red-600 hover:bg-red-50 inline-flex shrink-0 font-medium cursor-pointer"
                        onClick={onClearAll}
                    >
                        <X className="h-3.5 w-3.5 shrink-0" />
                        <span>Hủy lọc</span>
                        {activeFilterCount > 0 && (
                            <Badge className="h-4 px-1 text-[9px] ml-0.5 bg-red-500">{activeFilterCount}</Badge>
                        )}
                    </Button>
                ) : (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-[11px] sm:text-xs gap-1 inline-flex shrink-0 font-medium cursor-pointer"
                        onClick={onToggleFilter}
                    >
                        <Filter className="h-3.5 w-3.5 shrink-0" />
                        <span>Lọc</span>
                    </Button>
                )
            )}
        </>
    );
    const hasDateOrFilterToggle = !!dateFilter || (hasFilters && !!onToggleFilter);
    const showSecondFilterRow = Boolean(
        tabs
            ? (headerFilters || (forceTwoRows && hasDateOrFilterToggle))
            : (forceTwoRows && (headerFilters || hasDateOrFilterToggle))
    );

    return (
        // erp-list-shell: lớp bọc (VD module Kho) nhận diện để cho khung lấp đầy chiều cao còn lại thay vì 100vh-64px
        <div className={cn("erp-list-shell flex overflow-hidden", embedded ? "h-auto min-h-[400px]" : "h-[calc(100vh-64px)]")}>

            {/* Sidebar (GroupSidebar / StatusSidebar) */}
            {!embedded && sidebar}

            {/* Main area */}
            <div className="flex-1 flex flex-col min-w-0 bg-slate-50/40">

                {/* ── Sticky Toolbar 2 hàng (tabs + thao tác / ngày + lọc + tag) ─────── */}
                {!embedded && (
                <div
                    ref={toolbarRef}
                    className={cn(
                        'sticky top-0 z-30 bg-white border-b border-slate-200 shadow-2xs',
                        'transition-all duration-200 ease-in-out flex flex-col',
                        toolbarHidden ? '-translate-y-full' : 'translate-y-0',
                        isLoading && 'pointer-events-none opacity-80'
                    )}
                >
                    {primaryTabs && (
                        <div className="erp-list-primary-tabs border-b border-slate-100 px-3 overflow-x-auto hide-scrollbar">
                            {primaryTabs}
                        </div>
                    )}
                    {/* Chuẩn 2 hàng (Phase 176):
                        Hàng 1: tabs phẳng (trái) + cụm nút thao tác (phải); không đủ chỗ thì cụm nút xuống dòng, không bóp tabs.
                        Hàng 2 (khi có cả tabs và bộ lọc): ngày + Lọc + tag lọc / ô chọn, trọn chiều ngang.
                        Không có tabs: bộ lọc nằm trái hàng 1, thao tác bên phải (lọc trái - thao tác phải, Phase 67). */}
                    <div className="erp-list-toolbar-row flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 px-3 py-1.5 min-h-[38px] w-full">
                        {/* Tabs giữ trọn chiều rộng (shrink-0, tối đa 100%): thiếu chỗ thì cụm nút xuống dòng */}
                        <div className="erp-list-primary flex items-center gap-1.5 sm:gap-2 grow shrink-0 basis-auto max-w-full min-w-0 overflow-x-auto hide-scrollbar flex-nowrap">
                            {tabs ? (
                                <>
                                    <div className="erp-list-tabs flex items-center gap-1 shrink-0">
                                        {tabs}
                                    </div>
                                    {!showSecondFilterRow && hasDateOrFilterToggle && (
                                        <>
                                            <div className="h-5 w-px bg-slate-200 shrink-0 mx-0.5" />
                                            {dateAndFilterToggle}
                                        </>
                                    )}
                                </>
                            ) : forceTwoRows ? (
                                scopeLabel ? (
                                    <div className="erp-list-scope flex items-center gap-1.5 shrink-0 min-w-0">
                                        {scopeLabel}
                                    </div>
                                ) : null
                            ) : (
                                <>
                                    {dateAndFilterToggle}
                                    {headerFilters && (
                                        <div className="flex items-center gap-1.5 shrink-0 flex-nowrap [&>*]:flex-nowrap [&>*]:shrink-0">
                                            {headerFilters}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Bên phải: Cụm Nút Hành Động (Action Cluster) */}
                        {/* Không dùng justify-end: khi tràn, phần đầu bị đẩy ra ngoài và không cuộn tới được */}
                        <div ref={(el) => { actionsOverflow.ref(el); setActionsEl(el); }} className="erp-list-actions flex items-center gap-1.5 shrink-0 ml-auto flex-nowrap max-w-full overflow-x-auto hide-scrollbar">
                            {/* View Toggle */}
                            {availableViews && availableViews.length > 1 && onViewChange && (
                                <ToggleGroup type="single" size="sm" value={activeView} onValueChange={(v) => { if (v) onViewChange(v as 'table' | 'kanban' | 'calendar'); }} className="gap-0 border rounded-md shrink-0">
                                    {availableViews.includes('table') && (
                                        <ToggleGroupItem value="table" aria-label="Table view" className="h-7 w-7 px-0 data-[state=on]:bg-slate-100">
                                            <TableProperties className="h-3.5 w-3.5" />
                                        </ToggleGroupItem>
                                    )}
                                    {availableViews.includes('kanban') && (
                                        <ToggleGroupItem value="kanban" aria-label="Kanban view" className="h-7 w-7 px-0 data-[state=on]:bg-slate-100">
                                            <LayoutGrid className="h-3.5 w-3.5" />
                                        </ToggleGroupItem>
                                    )}
                                    {availableViews.includes('calendar') && (
                                        <ToggleGroupItem value="calendar" aria-label="Calendar view" className="h-7 w-7 px-0 data-[state=on]:bg-slate-100">
                                            <CalendarDays className="h-3.5 w-3.5" />
                                        </ToggleGroupItem>
                                    )}
                                </ToggleGroup>
                            )}

                            {/* Export Excel mặc định (bảng đang hiển thị) */}
                            {showAutoExport && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    data-testid="list-auto-export"
                                    className="h-7 px-2 text-[11px] sm:text-xs gap-1 shrink-0 font-medium cursor-pointer"
                                    onClick={exportVisibleTable}
                                    title="Xuất Excel các dòng đang hiển thị"
                                >
                                    <Download className="h-3.5 w-3.5 shrink-0" />
                                    <span>Xuất<span className="hidden sm:inline"> Excel</span></span>
                                </Button>
                            )}

                            {/* Export Excel */}
                            {onExport && (
                                <Button
                                    data-testid="btn-export-excel"
                                    variant="outline"
                                    size="sm"
                                    disabled={exportLoading}
                                    className="h-7 px-2 text-[11px] sm:text-xs gap-1 shrink-0 font-medium cursor-pointer"
                                    onClick={onExport}
                                >
                                    {exportLoading ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                                    ) : (
                                        <Download className="h-3.5 w-3.5 shrink-0" />
                                    )}
                                    <span>Xuất<span className="hidden sm:inline"> Excel</span></span>
                                </Button>
                            )}

                            {/* Secondary Actions ([··· Khác] trên Desktop — đặt trước nút chính để bảo toàn addLast === true) */}
                            {secondaryActions && secondaryActions.length > 0 && (
                                <ToolbarSecondaryActions items={secondaryActions} />
                            )}

                            {/* Custom Actions (+ Tạo mới, Import, Thao tác khác...) */}
                            {actions && (
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {actions}
                                </div>
                            )}

                            {/* Contextual Help Button (?) */}
                            <ContextualHelpButton />

                            <MobileOverflowMenu items={actionsOverflow.hidden} onSelect={actionsOverflow.activate} />
                        </div>
                    </div>

                    {showSecondFilterRow && (
                        <div className="erp-list-filter-row flex items-center gap-1.5 sm:gap-2 px-3 pb-1.5 pt-0.5 w-full overflow-x-auto hide-scrollbar flex-nowrap">
                            {dateAndFilterToggle}
                            {headerFilters && (
                                <div className="flex items-center gap-1.5 shrink-0 flex-nowrap [&>*]:flex-nowrap [&>*]:shrink-0">
                                    {headerFilters}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                )}

                {/* ── Optional Top Banner (KPI Metrics Ribbon, Summary Stats) ── */}
                {topBanner && (
                    <div className="px-3 py-1 bg-slate-50/60 border-b border-slate-200 shrink-0">
                        {topBanner}
                    </div>
                )}

                {/* ── Scrollable Table Area ───────────────────────────────── */}
                <div
                    ref={scrollRef}
                    className="flex-1 overflow-auto overscroll-contain relative erp-table-scroll"
                    style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
                >
                    <ListFetchingBar active={!isLoading && isAnyFetching} />
                    {isLoading && (
                        <div className="absolute inset-0 bg-white/40 z-20 flex items-center justify-center backdrop-blur-[1px]">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                    )}
                    {/* Horizontal scroll edge cues for desktop */}
                    {canScrollRight && (
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute top-0 right-0 bottom-0 w-8 bg-gradient-to-l from-slate-900/10 to-transparent z-[9] transition-opacity duration-200"
                        />
                    )}
                    {canScrollLeft && (
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute top-0 left-0 bottom-0 w-8 bg-gradient-to-r from-slate-900/10 to-transparent z-[9] transition-opacity duration-200"
                        />
                    )}
                    {/* The table receives sticky thead from CSS class */}
                    <div className={cn("erp-table-wrapper min-h-full", isLoading && "opacity-60 pointer-events-none transition-opacity duration-200")}>
                        <EntityLinkContext.Provider value={{ disableLink: true }}>
                            {children}
                        </EntityLinkContext.Provider>
                    </div>
                </div>

                {/* ── Sticky Pagination ──────────────────────────────────── */}
                {!inTrash && <ERPPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={onPageChange}
                    onPageSizeChange={onPageSizeChange}
                    unitLabel={unitLabel}
                />}
            </div>
        </div>
    );
}

// ── useListFilter hook — client-side filter + search helper ──────────────────

interface UseListFilterOptions<T> {
    data: T[];
    /** Fields to search in (dotted path supported, e.g. 'customer.name') */
    searchFields?: (keyof T | string)[];
    /** Pre-filter applied BEFORE search/advFilter (e.g. group sidebar selection) */
    preFilter?: (item: T) => boolean;
}

export function useListFilter<T extends Record<string, any>>({
    data,
    searchFields = [],
    preFilter,
}: UseListFilterOptions<T>) {
    // Read search from URL ?q param (set by Header)
    // IMPORTANT: useRouterState is reactive; useRouter().state is a snapshot!
    const routerSearch = useRouterState({ select: (s) => s.location.search }) as Record<string, any>;
    const urlQ: string = routerSearch?.q || '';
    const navigate = useNavigate();

    const currentPage = Number(routerSearch.page) || 1;
    // Không có ?pageSize (mở lại danh sách, quay về từ chi tiết) thì dùng số dòng người dùng đã chọn
    const preferredPageSize = usePreferredPageSize(25);
    const pageSize = Number(routerSearch.pageSize) || preferredPageSize;
    const sortField = (routerSearch.sortBy as string) || null;
    const sortOrder = routerSearch.sortDesc === 'true' || routerSearch.sortDesc === true ? 'desc' : (routerSearch.sortBy ? 'asc' : null) as SortOrder;

    const [filterValues, setFilterValues] = useState<ActiveFilters>({});
    const [showFilter, setShowFilter] = useState(false);

    const filtered = useMemo(() => {
        let result = preFilter ? data.filter(preFilter) : data;

        // URL-based quick search (Vietnamese diacritics aware)
        if (urlQ.trim()) {
            result = result.filter(item =>
                searchFields.some(field => {
                    const parts = String(field).split('.');
                    let val: any = item;
                    for (const p of parts) val = val?.[p];
                    return viIncludes(String(val ?? ''), urlQ);
                })
            );
        }

        // Advanced filter (áp dụng kể cả khi hàng lọc inline đang ẩn để chip/combobox header hoạt động)
        {
            Object.entries(filterValues).forEach(([key, val]) => {
                if (key.startsWith('_')) return;
                if (!val) return;
                if (typeof val === 'string' && val) {
                    result = result.filter(item => {
                        const parts = key.split('.');
                        let v: any = item;
                        for (const p of parts) v = v?.[p];
                        return viIncludes(String(v ?? ''), val);
                    });
                } else if (Array.isArray(val)) {
                    if (val.length > 0) {
                        result = result.filter(item => {
                            const parts = key.split('.');
                            let v: any = item;
                            for (const p of parts) v = v?.[p];
                            return val.includes(String(v ?? ''));
                        });
                    }
                } else if (typeof val === 'object' && val !== null) {
                    const { from, to } = val;
                    if (from || to) {
                        result = result.filter(item => {
                            const parts = key.split('.');
                            let v: any = item;
                            for (const p of parts) v = v?.[p];
                            const d = String(v ?? '');
                            if (from && d < from) return false;
                            if (to && d > to) return false;
                            return true;
                        });
                    }
                }
            });
        }

        return result;
    }, [data, urlQ, filterValues, searchFields, preFilter]);

    const handleFilter = useCallback((f: ActiveFilters) => {
        setFilterValues(f);
        (navigate as any)({ search: (prev: any) => ({ ...prev, page: 1 }), replace: true });
    }, [navigate]);

    // Clear all filters + header ?q search
    const clearAll = useCallback(() => {
        setFilterValues({});
        setShowFilter(false);
        (navigate as any)({ search: (prev: any) => ({ ...prev, q: undefined, page: 1, pageSize: undefined, sortBy: undefined, sortDesc: undefined }), replace: true });
    }, [navigate]);

    const toggleFilter = useCallback(() => {
        setShowFilter(prev => !prev);
    }, []);

    // ── Sort → Paginate ──
    const handleSort = useCallback((field: string, order: SortOrder) => {
        (navigate as any)({ search: (prev: any) => ({ ...prev, sortBy: order ? field : undefined, sortDesc: order === 'desc' ? true : undefined, page: 1 }), replace: true });
    }, [navigate]);

    const sortedFiltered = useMemo(() => {
        if (!sortField || !sortOrder) return filtered;
        return [...filtered].sort((a, b) => {
            let va: any = a, vb: any = b;
            if (sortField.includes('.')) {
                for (const p of sortField.split('.')) { va = va?.[p]; vb = vb?.[p]; }
            } else {
                va = a[sortField]; vb = b[sortField];
            }
            if (va == null && vb == null) return 0;
            if (va == null) return 1;
            if (vb == null) return -1;
            if (typeof va === 'number' && typeof vb === 'number') return sortOrder === 'asc' ? va - vb : vb - va;
            if (typeof va === 'string' && !isNaN(Date.parse(va))) {
                const da = new Date(va).getTime(), db = new Date(vb).getTime();
                if (!isNaN(da) && !isNaN(db)) return sortOrder === 'asc' ? da - db : db - da;
            }
            const sa = String(va).toLowerCase(), sb = String(vb).toLowerCase();
            return sortOrder === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
        });
    }, [filtered, sortField, sortOrder]);

    const totalItems = sortedFiltered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const safeCurrentPage = currentPage > totalPages ? 1 : currentPage;

    // Auto-sync URL ?page=1 when search/filter shrinks totalPages below currentPage (FB-MBXE4O, FB-O6RHNI)
    useEffect(() => {
        if (currentPage > totalPages && totalItems > 0) {
            (navigate as any)({ search: (prev: any) => ({ ...prev, page: 1 }), replace: true });
        }
    }, [currentPage, totalPages, totalItems, navigate]);

    const paginatedItems = useMemo(
        () => sortedFiltered.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize),
        [sortedFiltered, safeCurrentPage, pageSize]
    );

    const handleSearchChange = useCallback((q: string) => {
        (navigate as any)({ search: (prev: any) => ({ ...prev, q: q || undefined, page: 1 }), replace: true });
    }, [navigate]);

    const handlePageChange = useCallback((p: number) => {
        (navigate as any)({ search: (prev: any) => ({ ...prev, page: p }), replace: true });
    }, [navigate]);

    const handlePageSizeChange = useCallback((s: number) => {
        (navigate as any)({ search: (prev: any) => ({ ...prev, pageSize: s, page: 1 }), replace: true });
    }, [navigate]);

    return {
        // State
        searchQuery: urlQ,
        filterValues,
        currentPage: safeCurrentPage,
        pageSize,
        showFilter,
        // Sort
        sortField,
        sortOrder,
        handleSort,
        // Derived
        filtered: sortedFiltered,
        paginatedItems,
        totalItems,
        totalPages,
        // Setters
        onSearchChange: handleSearchChange,
        onFilterChange: handleFilter,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        onToggleFilter: toggleFilter,
        onClearAll: clearAll,
    };
}

// ── useServerListFilter hook — TRUE SERVER-SIDE pagination + filter + sort ────
// All params (search, status, group, sort, page, limit) are sent to the server.
// React Query key includes all params → auto-refetch on any change.
// Optional groupCountsQueryFn for lightweight sidebar counts.

import { useQuery, keepPreviousData } from '@tanstack/react-query';

export interface ServerListFilterOptions<T> {
    /** React Query key prefix, e.g. 'orders' */
    queryKey: any;
    /** API call function — receives server params, returns { items, meta } */
    queryFn: (params: ServerQueryParams) => Promise<{ items: T[]; meta: { total: number; totalPages: number; page: number; limit: number } }>;
    /** Default page size (default: 25) */
    defaultLimit?: number;
    /** Extra static params to always send, e.g. { customerId: 'xxx' } */
    extraParams?: Record<string, any>;
    /** Name of the API param for group filtering (e.g. 'customerId', 'typeId') */
    groupParamName?: string;
    /** Field name on items to match statusFilter against (default: 'status') */
    statusField?: string;
    /** Optional: fetch sidebar group counts separately (lightweight) */
    groupCountsQueryFn?: () => Promise<{ id: string; label: string; count: number }[]>;
}

export interface ServerQueryParams {
    page: number;
    limit: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    status?: string;
    [key: string]: any;
}

export function useServerListFilter<T extends Record<string, any>>({
    queryKey: keyPrefix,
    queryFn,
    defaultLimit = 100,
    extraParams = {},
    groupParamName,
    groupCountsQueryFn,
}: ServerListFilterOptions<T>) {
    const routerSearch = useRouterState({ select: (s) => s.location.search }) as Record<string, any>;
    const urlQ: string = routerSearch?.q || '';
    const navigate = useNavigate();

    const currentPage = Number(routerSearch.page) || 1;
    // Không có ?pageSize (mở lại danh sách, quay về từ chi tiết) thì dùng số dòng người dùng đã chọn
    const preferredPageSize = usePreferredPageSize(defaultLimit);
    const pageSize = Number(routerSearch.pageSize) || preferredPageSize;
    const sortField = (routerSearch.sortBy as string) || null;
    const sortOrder = routerSearch.sortDesc === 'true' || routerSearch.sortDesc === true ? 'desc' : (routerSearch.sortBy ? 'asc' : null) as SortOrder;

    const [filterValues, setFilterValues] = useState<ActiveFilters>({});
    const [showFilter, setShowFilter] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string[] | null>(null);
    const [groupFilter, setGroupFilter] = useState<string | null>(null);


    // Build server params — ALL filters go to server
    const serverParams = useMemo<ServerQueryParams>(() => {
        const params: ServerQueryParams = {
            page: currentPage,
            limit: pageSize,
            ...extraParams,
        };

        // Search from header ?q
        if (urlQ.trim()) {
            params.search = urlQ.trim();
        }

        // Status filter → send to server
        if (statusFilter && statusFilter.length > 0) {
            (params as any).status = statusFilter.length === 1 ? statusFilter[0] : statusFilter;
        }

        // Group filter → send to server (e.g. customerId=xxx)
        if (groupFilter && groupParamName) {
            // '_none' / '_ungrouped' → empty value meaning null on server
            if (groupFilter === '_none' || groupFilter === '_ungrouped' || groupFilter === 'Chưa phân nhóm') {
                params[groupParamName] = '_none';
            } else {
                params[groupParamName] = groupFilter;
            }
        }

        // Sort → send to server
        if (sortField && sortOrder) {
            params.sortBy = sortField;
            params.sortOrder = sortOrder;
        }

        // Advanced filters → pass to server
        // Luôn áp dụng bộ lọc đang có (kể cả khi hàng lọc inline bị ẩn) vì chip/combobox trên header
        // cũng ghi vào filterValues; nếu chỉ áp dụng khi hàng lọc hiển thị thì các chip sẽ không có tác dụng.
        {
            Object.entries(filterValues).forEach(([key, val]) => {
                if (key.startsWith('_') || !val) return;
                if (typeof val === 'string' && val) {
                    // 'ALL' là lựa chọn "Tất cả" của bộ lọc dạng select, không gửi lên máy chủ
                    if (val === 'ALL' || val === 'all') return;
                    params[key] = val;
                } else if (Array.isArray(val)) {
                    // Bộ lọc nhiều lựa chọn: 1 giá trị gửi dạng chuỗi, nhiều giá trị gửi dạng mảng
                    const list = (val as string[]).filter(Boolean);
                    if (list.length === 1) params[key] = list[0];
                    else if (list.length > 1) params[key] = list;
                } else if (typeof val === 'object') {
                    const { from, to } = val as { from?: string; to?: string };
                    if (from) params[`${key}From`] = from;
                    if (to) params[`${key}To`] = to;
                }
            });
        }

        return params;
    }, [currentPage, pageSize, urlQ, filterValues, extraParams, statusFilter, groupFilter, groupParamName, sortField, sortOrder]);

    const normalizedKey = Array.isArray(keyPrefix) ? keyPrefix : [keyPrefix];

    // React Query — ALL changes trigger server re-fetch
    const { data, isLoading, isFetching, error, refetch } = useQuery({
        queryKey: [...normalizedKey, serverParams],
        queryFn: () => queryFn(serverParams),
        placeholderData: keepPreviousData, // Smooth transitions, no flash
        // Quay lại danh sách sau khi xoá/sửa ở trang chi tiết luôn tải lại (vẫn hiện bản cũ trong lúc tải), không phải tải lại trang (FB-7KL5H3)
        refetchOnMount: 'always',
    });

    // Optional: sidebar group counts (separate lightweight query)
    const { data: groupCounts, isLoading: isGroupLoading } = useQuery({
        queryKey: [...normalizedKey, 'group-counts'],
        queryFn: groupCountsQueryFn || (() => Promise.resolve([])),
        staleTime: 30 * 1000, // 30s cache for sidebar counts
        refetchOnMount: 'always',
        enabled: !!groupCountsQueryFn,
    });

    // Items from server (already paginated + filtered + sorted)
    const displayItems: T[] = (data as any)?.items || [];
    const meta = (data as any)?.meta || { total: 0, totalPages: 1, page: 1, limit: pageSize };
    const totalItems = meta.total;
    const totalPages = meta.totalPages || Math.max(1, Math.ceil(totalItems / pageSize));

    // ── Sort handler (triggers server re-fetch via params) ──
    const handleSort = useCallback((field: string, order: SortOrder) => {
        (navigate as any)({ search: (prev: any) => ({ ...prev, sortBy: order ? field : undefined, sortDesc: order === 'desc' ? true : undefined, page: 1 }), replace: true });
    }, [navigate]);

    // Filter change handler
    const handleFilter = useCallback((f: ActiveFilters) => {
        setFilterValues(f);
        (navigate as any)({ search: (prev: any) => ({ ...prev, page: 1 }), replace: true });
    }, [navigate]);

    // Group sidebar handler → triggers server re-fetch
    const handleGroupChange = useCallback((group: string | null) => {
        setGroupFilter(group);
        (navigate as any)({ search: (prev: any) => ({ ...prev, page: 1 }), replace: true });
    }, [navigate]);

    // Clear all filters + header ?q search + status + group
    const clearAll = useCallback(() => {
        setFilterValues({});
        setShowFilter(false);
        setStatusFilter(null);
        setGroupFilter(null);
        (navigate as any)({ search: (prev: any) => ({ ...prev, q: undefined, page: 1, pageSize: undefined, sortBy: undefined, sortDesc: undefined }), replace: true });
    }, [navigate]);

    const toggleFilter = useCallback(() => {
        setShowFilter(prev => !prev);
    }, []);

    // Status counts from server response meta
    const statusCounts = (data as any)?.meta?.statusCounts || [] as { status: string; count: number }[];

    const handleSearchChange = useCallback((q: string) => {
        (navigate as any)({ search: (prev: any) => ({ ...prev, q: q || undefined, page: 1 }), replace: true });
    }, [navigate]);

    const handlePageChange = useCallback((p: number) => {
        (navigate as any)({ search: (prev: any) => ({ ...prev, page: p }), replace: true });
    }, [navigate]);

    const handlePageSizeChange = useCallback((s: number) => {
        (navigate as any)({ search: (prev: any) => ({ ...prev, pageSize: s, page: 1 }), replace: true });
    }, [navigate]);

    return {
        // Data
        items: displayItems,            // Items from server (current page)
        paginatedItems: displayItems,    // Backward compat alias
        filtered: displayItems,          // Backward compat alias
        groupCounts: groupCounts || [],  // Sidebar group counts
        statusCounts,                    // Status counts for tags/sidebar
        meta,                            // Expose the raw meta object for custom aggregations
        isLoading,
        isFetching,                      // Đang tải lại (lọc/sắp xếp/phân trang) - dùng cho chỉ báo loading
        isGroupLoading,
        error,
        refetch,
        // State
        searchQuery: urlQ,
        filterValues,
        serverParams,
        currentPage,
        pageSize,
        showFilter,
        statusFilter,
        groupFilter,
        totalItems,
        totalPages,
        // Sort
        sortField,
        sortOrder,
        handleSort,
        // Setters
        onSearchChange: handleSearchChange,
        onFilterChange: handleFilter,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        onToggleFilter: toggleFilter,
        onClearAll: clearAll,
        onStatusChange: (status: string | string[] | null) => {
            if (Array.isArray(status)) {
                setStatusFilter(status.length ? status : null);
            } else {
                setStatusFilter(status ? (statusFilter?.includes(status) 
                    ? statusFilter.filter(s => s !== status) 
                    : [...(statusFilter || []), status]) : null);
            }
            (navigate as any)({ search: (prev: any) => ({ ...prev, page: 1 }), replace: true });
        },
        onGroupChange: handleGroupChange,
    };
}

